import {
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
} from "@mui/material";
import { useEffect, useState } from "react";

import FormulaDropDown from "@/components/membership/ChooseFormulaModal/ChooseFormulaComponents/FormulaDropdown";
import LookupForm from "@/components/membership/ChooseFormulaModal/lookupForm/LookupForm";
import LookupTableUtils from "@/components/membership/ChooseFormulaModal/lookupForm/LookupUtils/LookupTableUtils";
import SiteIcons from "@/components/SiteIcons/SiteIcons";

const ChooseFormulaModal = ({
  row,
  rowIndex,
  saveFormula,
  openLookupDirectly,
  formulae,
}) => {
  const {
    addRow,
    deleteRow,
    addColumn,
    addHeader,
    deleteColumn,
    deleteHeader,
    getEmptyLookupTable,
    getEmptyLookupColumnsArray,
  } = LookupTableUtils();
  const [showLookupForm, setShowLookupForm] = useState(false);
  const [formulaData, setFormulaData] = useState({
    formula: row.formula,
    formula_data: row.formula_data,
    affiliate_columns: row.affiliate_columns,
  });
  const [open, setOpen] = useState(false);
  const [disableContinueButton, setDisableContinueButton] = useState(false);
  const permittedOutputs = row.permitted_outputs ?? [];
  const adaName = row.name;
  const recommendedFormulae = row.formulae;

  // TODO all this table logic and functionality needs to refactored into its own hook, but I'm sick of working on this ticket.

  // make sure Save Formula button is always enabled if non-lookup formula is chosen
  useEffect(() => {
    if (!showLookupForm) {
      setDisableContinueButton(false);
    }
  }, [showLookupForm]);

  useEffect(() => {
    setShowLookupForm(formulaData.formula === "lookup");
    // set up table
    if (formulaData.formula === "lookup") {
      const initialTable =
        row.formula_data && row.formula_data.length > 0
          ? row.formula_data
          : getEmptyLookupTable();

      const initialColumns =
        row.affiliate_columns && row.affiliate_columns.length > 0
          ? row.affiliate_columns
          : getEmptyLookupColumnsArray();

      setFormulaData({
        formula: "lookup",
        formula_data: initialTable,
        affiliate_columns: initialColumns,
      });
    }
    // getEmptyLookupTable and getEmptyLookupColumnsArray are recreated by
    // LookupTableUtils() each render; row.formula_data/affiliate_columns are
    // already covered via formulaData.formula's effect on these branches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formulaData.formula]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const updateFullTable = (newTableData, newColumnsArray = null) => {
    setFormulaData((prev) => ({
      ...prev,
      formula_data: newTableData,
      // if the CSV upload provides headers, update them too; otherwise keep existing
      affiliate_columns: newColumnsArray || prev.affiliate_columns,
    }));
  };

  const updateTableValues = (tableRowIndex, rowData) => {
    setFormulaData((prev) => ({
      ...prev,
      formula_data: prev.formula_data.map((row, i) =>
        i === tableRowIndex ? rowData : row
      ),
    }));
  };

  const updateLookupColumns = (newColumnsArray) => {
    setFormulaData((prev) => ({
      ...prev,
      affiliate_columns: [...newColumnsArray],
    }));
  };

  const handleFormulaChange = (formula) => {
    setShowLookupForm(formula.name === "lookup");
    const formulaObject = {
      formula: formula.name,
      formula_data: formula.name === "lookup" ? formulaData.formula_data : null,
      affiliate_columns:
        formula.name === "lookup" ? formulaData.affiliate_columns : null,
    };
    setFormulaData(formulaObject);
  };

  const handleSaveClick = () => {
    saveFormula(rowIndex, formulaData);
    handleClose();
  };

  const alterTable = (action) => {
    setFormulaData((prev) => {
      // references to current state
      const currentRows = prev.formula_data;
      const currentCols = prev.affiliate_columns;

      switch (action) {
        case "add row":
          return { ...prev, formula_data: addRow(currentRows) };

        case "delete row":
          return { ...prev, formula_data: deleteRow(currentRows) };

        case "add column":
          return {
            ...prev,
            formula_data: addColumn(currentRows),
            affiliate_columns: addHeader(currentRows, currentCols),
          };

        case "delete column":
          return {
            ...prev,
            formula_data: deleteColumn(currentRows),
            affiliate_columns: deleteHeader(currentRows, currentCols),
          };

        default:
          return prev;
      }
    });
  };

  return (
    <>
      {openLookupDirectly ? (
        // button next to mapped column names to directly edit lookup table
        <Button className="siteBlue" onClick={handleClickOpen}>
          edit lookup
        </Button>
      ) : (
        <IconButton aria-label="edit" onClick={handleClickOpen}>
          <SiteIcons iconstring="edit" />
        </IconButton>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        id="edit-formula-modal"
        data-testid="edit-formula-modal"
        slotProps={{
          paper: {
            sx: {
              maxWidth: "90vw", // prevents overflow on small screens
            },
          },
        }}
      >
        <DialogTitle>Formula for {adaName}</DialogTitle>
        <DialogContent>
          <Box sx={{ width: "250px", marginBottom: "10px" }}>
            <Typography>
              Formula: <b>{formulaData.formula}</b>
            </Typography>
            {formulae && (
              <Typography>
                Description: {formulae[formulaData.formula]["description"]}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex" }}>
            <Box>
              <FormulaDropDown
                currentFormula={formulaData.formula}
                handleFormulaChange={handleFormulaChange}
                recommended={recommendedFormulae}
              />
            </Box>
            <Box sx={{ marginLeft: "10px" }}>
              {" "}
              {showLookupForm && (
                <LookupForm
                  lookupColumns={formulaData.affiliate_columns}
                  updateLookupColumns={updateLookupColumns}
                  tableValues={formulaData.formula_data}
                  updateTableValues={updateTableValues}
                  setDisableContinueButton={setDisableContinueButton}
                  permittedOutputs={permittedOutputs}
                  alterTable={alterTable}
                  updateFullTable={updateFullTable}
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={handleSaveClick}
            disabled={disableContinueButton}
          >
            Save Formula
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChooseFormulaModal;
